import React, { useEffect, useState, useCallback, memo } from 'react';
import { divisionLevelHeatMap } from '../../../../services/api-services';
import { toPng } from 'html-to-image';
import Remark from '../Remark';
import DownloadGraphButton from '../../../../user/pages/page/DownloadGraphButton';
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const JobLeveHeatmap = memo(({ selectedCompanyId, graphName, myhandleExport }) => {
    const [divisionHeatmepData, setDivisionHeatmepData] = useState([]);
    const [remark, setRemark] = useState('');
    const [remarkId, setRemarkId] = useState(null);
    const [isAttempt, setIsAttempt] = useState('');
    const [colorRanges, setColorRanges] = useState([]);

    // Fetch heatmap data
    useEffect(() => {
        const getHeatmapdivisionData = async () => {
            try {
                const response = await divisionLevelHeatMap(selectedCompanyId, 'hierarchy', graphName);
                if (response?.data?.responseCode === 200) {
                    setRemark(response?.data?.remark || '');
                    setIsAttempt(response?.data?.isAttempt ?? 0);
                    setRemarkId(response?.data?.remarkId || null);
                    setDivisionHeatmepData(response?.data?.responseBody || []);
                    setColorRanges(response?.data?.companyColorList || []);
                }
            } catch (err) {
                console.error('Error fetching division heatmap data:', err);
            }
        };

        if (selectedCompanyId) {
            getHeatmapdivisionData();
        }
    }, [selectedCompanyId, graphName]);

    // Memoized function to get color based on percentage
    const getColorForPercentage = useCallback(
        (percentage) => {
            const range = colorRanges.find(
                (r) => percentage >= r.rangeFrom && percentage <= r.rangeTo
            );
            return range ? range.colorName : '#557D39';
        },
        [colorRanges]
    );

    // Memoized legend rendering
    const renderLegend = useCallback(() => (
        <div className="legend-container">
            <div className="legend-row">
                {colorRanges.map((item, index) => (
                    <div key={index} className="legend-item">
                        <div className="legend-color" style={{ backgroundColor: item.colorName }}></div>
                        <span className="legend-label">{`${item.rangeFrom}% - ${item.rangeTo}%`}</span>
                    </div>
                ))}
            </div>
        </div>
    ), [colorRanges]);

    // Generate image and update local storage
    const generateHtmlImage = useCallback(async (elementId, remarkValue) => {
        const node = document.getElementById(elementId);
        if (!node) return;

        try {
            const dataUrl = await toPng(node);
            const chartData = JSON.parse(localStorage.getItem('chartImageData')) || [];
            const updatedChartData = chartData.map((item) =>
                item.id === 'demographic_dashboard_job_level2'
                    ? { ...item, image: dataUrl, remark: remarkValue }
                    : item
            );
            localStorage.setItem('chartImageData', JSON.stringify(updatedChartData));
        } catch (error) {
            console.error('Error generating image from HTML:', error);
        }
    }, []);

    const exportToExcel = (data, fileName = "Demographic Dashboard – (Division Level)") => {
        if (!data || !Array.isArray(data)) return;

        const title = ["Demographic Dashboard – (Division Level)"];
        const headers = [
            "Hierarchy Name",
            "Dimension Name",
            "Dimension Percentage",
            "Subdimension Name",
            "Subdimension Percentage"
        ];

        const rows = [];

        data.forEach(dept => {
            dept.dimensionOverallHeatmapChartResponseList?.forEach(dimension => {
                dimension.subDimensionOverallHeatmapChartResponseList?.forEach(sub => {
                    rows.push([
                        dept.name,
                        dimension.dimensionName,
                        dimension.percentage,
                        sub.subDimensionName,
                        sub.percentage
                    ]);
                });
            });
        });

        const worksheet = XLSX.utils.aoa_to_sheet([
            title,
            [],
            headers,
            ...rows
        ]);

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Department Heatmap");

        const excelBuffer = XLSX.write(workbook, {
            bookType: "xlsx",
            type: "array"
        });

        const blob = new Blob([excelBuffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        });

        saveAs(blob, fileName);
    };


    return (
        <div className="container-fluid" id="demographic_dashboard_job_level2">
            <div>
                {divisionHeatmepData.length > 0 ? (
                    divisionHeatmepData.map((division, divisionIndex) => {
                        const validDimensions = division.dimensionOverallHeatmapChartResponseList?.filter(
                            (dimension) => dimension.percentage >= 0
                        );
                        if (!validDimensions?.length) return null;

                        return (
                            <div
                                key={divisionIndex}
                                className={`pdf-chart division-section mt-4 chart14-${divisionIndex}`}
                                data-heading="Overall Participation (Job Level)"
                                data-remark={remark}
                            >
                                <div className="d-flex justify-content-between">
                                    <h5>{division.name}</h5>
                                    <div className='d-flex'>
                                        <DownloadGraphButton chartID={`chart14-${divisionIndex}`} myhandleExport={myhandleExport} />
                                        <button className="px-3 py-2 mb-3 text-white bg-primary rounded border-0 ms-2" onClick={() => exportToExcel(divisionHeatmepData)}>Download Excel</button>
                                    </div>
                                </div>
                                {renderLegend()}
                                <div className="header-row">
                                    {validDimensions.map((dimension) => (
                                        <div
                                            key={dimension.id}
                                            className="header-cell"
                                            style={{ backgroundColor: getColorForPercentage(dimension.percentage?.toFixed(2)) }}
                                        >
                                            <p>{dimension.dimensionName}</p>
                                            <p>{dimension.percentage?.toFixed(2)}%</p>
                                        </div>
                                    ))}
                                </div>
                                <div className="data-row mb-2">
                                    {validDimensions.map((dimension) => (
                                        <div key={dimension.id} className="column">
                                            {dimension.subDimensionOverallHeatmapChartResponseList?.map((subDimension) => (
                                                <div
                                                    key={subDimension.id}
                                                    className="data-cell"
                                                    style={{ backgroundColor: getColorForPercentage(subDimension.percentage) }}
                                                >
                                                    <p>{subDimension.subDimensionName}</p>
                                                    <p>{subDimension.percentage?.toFixed(2)}%</p>
                                                </div>
                                            ))}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })
                ) : (
                    <p style={{ textAlign: 'center', marginTop: '20px' }}>No data available to display.</p>
                )}
            </div>
            <Remark
                selectedCompanyId={selectedCompanyId}
                graphName="DemographicDashboard"
                initialRemark={remark}
                remarkId={remarkId}
                isAttempt={isAttempt}
                setRemarkprops={setRemark}
            />
        </div>
    );
});

export default JobLeveHeatmap;
